import React, { useCallback, useContext, useMemo } from 'react';
import { ForgeList, ForgeListItem } from '@tylertech/forge-react';
import { IListItemSelectEventData } from '@tylertech/forge/esm/list';
import { FilterFooter } from './FilterFooter';
import { SoQLType } from 'common/types/soql';
import { formatDate } from 'common/dates';
import { getAllSpecificValues } from './lib/Filters/parameterUtils';
import { ParameterItemContext } from './ParameterItem/ParameterItemContext';

export const ParameterDropdown = ({
  onReset,
  onUpdate,
}: {
  onUpdate?: (value: string) => void;
  onReset?: () => void;
}) => {
  const { parameter, clientContextVariables } = useContext(ParameterItemContext)!;

  const onSelectParameterOverride = useCallback(
    (event: CustomEvent<IListItemSelectEventData>) => {
      onUpdate?.(event.detail.value);
    },
    [onUpdate],
  );

  const allValues = useMemo(() => {
    const allSpecificValues = getAllSpecificValues(parameter.paramIds, clientContextVariables);

    // If a specific value does not have a displayName defined, then use the value for the displayName
    return allSpecificValues.map((specificValue) => {
      if (specificValue.displayName) {
        return specificValue;
      }
      // For date parameters, format the value so it doesn't appear in ISO format
      const displayName =
        parameter.dataType === SoQLType.SoQLFloatingTimestampT
          ? formatDate(specificValue.value, 'l')
          : specificValue.value;
      return { value: specificValue.value, displayName };
    });
  }, [clientContextVariables, parameter.dataType, parameter.paramIds]);

  return (
    <div className="parameter-dropdown-options">
      <ForgeList dense selectedValue={parameter.overrideValue}>
        {allValues.map((value) => (
          <ForgeListItem
            key={value.value}
            value={value.value}
            data-testid={`${value.value}-parameter-option`}
            on-forge-list-item-select={onSelectParameterOverride}
          >
            <button>{value.displayName}</button>
          </ForgeListItem>
        ))}
      </ForgeList>
      <FilterFooter onClickReset={onReset} showApplyButton={false} />
    </div>
  );
};
